import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Radio,
  Input,
  Switch,
  Tooltip,
  InputNumber,
  Select,
} from 'antd';
import { Translation, getI18n } from 'react-i18next';
import ConfirmationModal from '../../../elements/components/ConfirmationModal';
import Logger from '../../../../../lib/Logger';
import notification from '../../../elements/lib/NotificationWrapper';
import RegistrationCodeInput from '../../registrationCodes/containers/RegistrationCodeInputContainer';
import CareGuidePartnerTagInput from '../../careGuidePartnerTagsAdmin/containers/CareGuidePartnerTagInputContainer';
import PublicStatusInput from './PublicStatusInput';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { mixpanelEventsEnum } from '../../../../../lib/Mixpanel';

const layout = {
  main: {
    labelCol: { span: 24 },
  },
};

const confirmTexts = {
  title: getI18n().t('cognito_form_admin_confirm_modal_title'),
  description: getI18n().t('cognito_form_admin_confirm_modal_description'),
  leaveText: getI18n().t('cognito_form_admin_confirm_modal_leave'),
  saveText: getI18n().t('cognito_form_admin_confirm_modal_return'),
};

const optionsTypes = [
  {
    label: getI18n().t('cognito_forms_admin_tab_onboarding_form'),
    value: 1,
  },
  {
    label: getI18n().t('cognito_forms_admin_tab_internal_forms'),
    value: 2,
  },
  {
    label: getI18n().t('cognito_forms_admin_tab_surveys'),
    value: 3,
  },
];

const CognitoFormModalFormAdmin = ({
  id,
  data,
  errors,
  load,
  destroyForm,
  isSubmitting,
  isLoading,
  partnershipId,
  page,
  limit,
  filter,
  order,
  remove,
  inputs,
  trackerActions,
  ...props
}) => {
  const [form] = Form.useForm();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isChangeValue, setIsChangeValue] = useState(false);
  const [type, setType] = useState(1);
  const [switchNewUsers, setSwitchNewUsers] = useState(false);
  const [switchOldUsers, setSwitchOldUsers] = useState(false);
  const [oldUserRequirement, setOldUserRequirement] = useState(null);
  const [oldUserHowOften, setOldUserHowOften] = useState(null);

  const submitData = async (values) => {
    Logger.log('debug', `CognitoFormAdminForm.submitData(###)`);

    // API POST/PUT payload
    let payload = {};
    let cognito_forms_care_guide_partner_tags = [];
    for (const input of Object.keys(data)) {
      if (input in values) {
        if (input === 'cognito_forms_care_guide_partner_tags') {
          if (Array.isArray(values[input])) {
            const tagsFilters = values[input].filter((item) => item !== '');
            cognito_forms_care_guide_partner_tags =
              cognito_forms_care_guide_partner_tags.concat(tagsFilters);
          } else {
            if (values[input] !== '') {
              cognito_forms_care_guide_partner_tags.push(values[input]);
            }
          }
        } else {
          payload[input] = values[input];
        }
      }
    }
    payload['cognito_forms_care_guide_partner_tags_list'] =
      cognito_forms_care_guide_partner_tags;

    payload['partnership_id'] = partnershipId;
    delete payload['cognito_forms_care_guide_partner_tags'];

    if (id) {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.COGNITO_FORMS_ADMIN_EDIT_SUBMIT
      );
      // update
      props.update(id, payload, (success) => {
        if (success) {
          notification.success(getI18n().t('feedback_form_success'));
          props.loadList(partnershipId, page, limit, order, filter);
          clearForm();
        } else {
          notification.error(getI18n().t('feedback_form_error'));
        }
      });
    } else {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.COGNITO_FORMS_ADMIN_ADD_SUBMIT
      );
      // create
      props.create(payload, (success) => {
        if (success) {
          props.loadList(partnershipId, page, limit, order, filter);
          notification.success(getI18n().t('feedback_form_success'));
          clearForm();
        } else {
          notification.error(getI18n().t('feedback_form_error'));
        }
      });
    }
  };

  const onConfirmModal = () => {
    props.hideForm();
    setConfirmationModalOpen(true);
  };

  const onCancelConfirmModal = () => {
    setConfirmationModalOpen(false);
    props.showForm(id);
  };

  const onLeaveConfirmModal = () => {
    setConfirmationModalOpen(false);
    clearForm();
  };

  const onSaveConfirmModal = () => {
    setConfirmationModalOpen(false);
    props.showForm(id);
  };

  const clearForm = () => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.COGNITO_FORMS_ADMIN_CLOSE_MODAL_FORM
    );
    props.hideForm();
    destroyForm();
    setIsChangeValue(false);
    setType(1);
    setSwitchNewUsers(false);
    setSwitchOldUsers(false);
    setOldUserHowOften(null);
    setOldUserRequirement(null);
    form.setFieldsValue({
      type: 1,
      cognito_form_id: null,
      registration_code_id: null,
      public_status: 1,
      cognito_forms_care_guide_partner_tags: [],
      new_user_requirement: null,
      old_user_requirement: null,
      old_user_requirement_frequency_type: null,
      old_user_requirement_repeat_every: null,
      old_user_requirement_repeat_unit: null,
      name: '',
      form_id: '',
      form_key: '',
    });
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `CognitoFormModalFormAdmin.handleFinish(###)`);
    if (!isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `CognitoFormModalFormAdmin.handleFinishFailed(###)`);
    notification.error(getI18n().t('cognito_form_admin_message_form_failure'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  // remove error notification when input value changes
  const handleValuesChange = (changedValues, allValues) => {
    setIsChangeValue(true);
    for (const key of Object.keys(changedValues)) {
      form.setFields([{ name: key, errors: [] }]);
    }
  };

  const onClickOK = () => {
    form.submit();
  };

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);

  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      setType(dataObj['type']);

      const old_user_requirement = dataObj['old_user_requirement'];

      setSwitchNewUsers(dataObj['new_user_requirement'] ? true : false);
      setSwitchOldUsers(old_user_requirement ? true : false);
      setOldUserRequirement(old_user_requirement);
      setOldUserHowOften(dataObj['old_user_requirement_frequency_type']);

      const careGuidePartnerTagIds = dataObj[
        'cognito_forms_care_guide_partner_tags'
      ].map((tag) => tag.id);
      dataObj['cognito_forms_care_guide_partner_tags'] = careGuidePartnerTagIds;
      form.setFieldsValue(dataObj);
    }
  }, [form, dataString, isSubmitting, id]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // load record data from API
  useEffect(() => {
    if (id) {
      load(partnershipId, id);
    }
  }, [partnershipId, id, load]);

  return (
    <Translation>
      {(t) => (
        <>
          <Modal
            title={
              id
                ? t('cognito_form_admin_modal_header_title_edit')
                : t('cognito_form_admin_modal_header_title_new')
            }
            closable={false}
            className='cognito-form-modal'
            centered
            visible={props.visible}
            onOk={onClickOK}
            onCancel={isChangeValue ? onConfirmModal : clearForm}
            footer={[
              <Button
                key='back'
                onClick={isChangeValue ? onConfirmModal : clearForm}
              >
                {t('cognito_form_admin_modal_cancel')}
              </Button>,
              <Button
                key='submit'
                type='primary'
                className='btn-save'
                loading={isSubmitting}
                onClick={onClickOK}
              >
                {t('cognito_form_admin_modal_save')}
              </Button>,
            ]}
          >
            <div style={{ overflowY: 'auto', maxHeight: '700px' }}>
              <Form
                form={form}
                name='cognito_form'
                {...layout.main}
                initialValues={data}
                onFinish={handleFinish}
                onFinishFailed={handleFinishFailed}
                onValuesChange={handleValuesChange}
                validateTrigger='onSubmit'
                className='cognito-form-modal-form'
              >
                <div className='form-group'>
                  <Form.Item
                    name='name'
                    label={t('cognito_form_admin_name')}
                    rules={[
                      {
                        required: true,
                        message: t('feedback_validation_required'),
                      },
                      {
                        type: 'string',
                        min: 2,
                        max: 100,
                        message: t('feedback_validation_char_range', {
                          min: 2,
                          max: 100,
                        }),
                      },
                    ]}
                  >
                    <Input disabled={isLoading || isSubmitting} />
                  </Form.Item>
                </div>

                <div className='form-group'>
                  <Form.Item
                    name='form_id'
                    label={t('cognito_form_admin_form_id')}
                    placeholder={t('cognito_form_admin_form_id_placeholder')}
                    rules={[
                      {
                        required: true,
                        message: t('feedback_validation_required'),
                      },
                      {
                        type: 'string',
                        min: 1,
                        max: 10,
                        message: t('feedback_validation_char_range', {
                          min: 1,
                          max: 10,
                        }),
                      },
                    ]}
                  >
                    <Input disabled={isLoading || isSubmitting} />
                  </Form.Item>
                </div>

                <div className='form-group'>
                  <Form.Item
                    name='form_key'
                    label={t('cognito_form_admin_form_key')}
                    placeholder={t('cognito_form_admin_form_key_placeholder')}
                    rules={[
                      {
                        required: true,
                        message: t('feedback_validation_required'),
                      },
                      {
                        type: 'string',
                        min: 1,
                        max: 40,
                        message: t('feedback_validation_char_range', {
                          min: 1,
                          max: 40,
                        }),
                      },
                    ]}
                  >
                    <Input disabled={isLoading || isSubmitting} />
                  </Form.Item>
                </div>
                <PublicStatusInput
                  name='public_status'
                  label={t('cognito_form_admin_public_status')}
                  rules={[
                    {
                      required: true,
                      message: t('feedback_validation_required'),
                    },
                  ]}
                  disabled={isLoading || isSubmitting}
                />
                <div className='form-group'>
                  <Form.Item
                    name='type'
                    label={t('cognito_form_admin_cognito_form_type')}
                  >
                    <Radio.Group
                      options={optionsTypes}
                      onChange={(e) => setType(e.target.value)}
                    />
                  </Form.Item>
                </div>
                {type === 1 && (
                  <>
                    <RegistrationCodeInput
                      name='registration_code_id'
                      rules={[
                        {
                          required: true,
                          message: t('feedback_validation_required'),
                        },
                      ]}
                      disabled={isLoading || isSubmitting}
                      label='cognito_form_admin_registration_code'
                      filter={{ type_user: [3] }}
                    />
                    <div
                      className='ant-collapse ant-collapse-icon-position-left'
                      style={{
                        backgroundColor: 'white',
                        marginTop: '20px',
                      }}
                    >
                      <div className='ant-collapse-item ant-collapse-item-active ant-collapse-no-arrow'>
                        <div
                          className='ant-collapse-header'
                          role='button'
                          tabindex='0'
                          aria-expanded='true'
                          style={{
                            cursor: 'auto',
                          }}
                        >
                          <span className='ant-typography'>
                            {t(
                              'cognito_form_admin_cognito_form_label_new_users'
                            )}
                          </span>

                          <div className='ant-collapse-extra'>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Switch
                                checked={switchNewUsers}
                                onChange={setSwitchNewUsers}
                              />
                            </div>
                          </div>
                        </div>
                        {switchNewUsers && (
                          <div
                            className='ant-collapse-content ant-collapse-content-active'
                            style={{
                              width: '94%',
                              margin: '0 auto',
                              borderLeft: 'none',
                              borderRight: 'none',
                              borderBottom: 'none',
                            }}
                          >
                            <div className='ant-collapse-content-box'>
                              <div className='ant-form-item-label'>
                                <label
                                  className='ant-form-item-required'
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  {t(
                                    'cognito_form_admin_cognito_form_label_requirement'
                                  )}
                                </label>
                              </div>
                              <div className='form-group'>
                                <Form.Item
                                  name='new_user_requirement'
                                  label={null}
                                  style={{ marginBottom: '0' }}
                                >
                                  <Radio.Group>
                                    <Radio value={1}>
                                      {t(
                                        'cognito_form_admin_cognito_form_new_user_required'
                                      )}
                                    </Radio>
                                    <Radio value={2}>
                                      {t(
                                        'cognito_form_admin_cognito_form_new_user_optional'
                                      )}
                                    </Radio>
                                  </Radio.Group>
                                </Form.Item>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className='ant-collapse ant-collapse-icon-position-left'
                      style={{
                        backgroundColor: 'white',
                        marginTop: '20px',
                      }}
                    >
                      <div className='ant-collapse-item ant-collapse-item-active ant-collapse-no-arrow'>
                        <div
                          className='ant-collapse-header'
                          role='button'
                          tabindex='0'
                          aria-expanded='true'
                          style={{
                            cursor: 'auto',
                          }}
                        >
                          <span className='ant-typography'>
                            {t(
                              'cognito_form_admin_cognito_form_label_old_users'
                            )}
                          </span>

                          <div className='ant-collapse-extra'>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Switch
                                checked={switchOldUsers}
                                onChange={setSwitchOldUsers}
                              />
                            </div>
                          </div>
                        </div>
                        {switchOldUsers && (
                          <div
                            className='ant-collapse-content ant-collapse-content-active'
                            style={{
                              width: '94%',
                              margin: '0 auto',
                              borderLeft: 'none',
                              borderRight: 'none',
                              borderBottom: 'none',
                            }}
                          >
                            <div className='ant-collapse-content-box'>
                              <div className='ant-form-item-label'>
                                <label
                                  className='ant-form-item-required'
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  {t(
                                    'cognito_form_admin_cognito_form_label_requirement'
                                  )}
                                </label>
                              </div>

                              <div className='form-group'>
                                <Form.Item
                                  name='old_user_requirement'
                                  label={null}
                                  style={{ marginBottom: '0' }}
                                >
                                  <Radio.Group
                                    onChange={(e) =>
                                      setOldUserRequirement(e.target.value)
                                    }
                                  >
                                    <Radio value={1}>
                                      {t(
                                        'cognito_form_admin_cognito_form_old_user_required'
                                      )}
                                    </Radio>
                                    <Radio value={2}>
                                      {t(
                                        'cognito_form_admin_cognito_form_old_user_optional'
                                      )}
                                    </Radio>
                                  </Radio.Group>
                                </Form.Item>
                              </div>

                              {oldUserRequirement === 1 && (
                                <>
                                  <div className='ant-form-item-label'>
                                    <label
                                      className='ant-form-item-required'
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {t(
                                        'cognito_form_admin_cognito_form_label_how_often'
                                      )}
                                      <Tooltip
                                        title={t(
                                          'cognito_form_admin_cognito_form_label_how_often_tooltip'
                                        )}
                                      >
                                        <QuestionCircleOutlined
                                          style={{ marginLeft: 4 }}
                                        />{' '}
                                      </Tooltip>
                                    </label>
                                  </div>

                                  <div className='form-group'>
                                    <Form.Item
                                      name='old_user_requirement_frequency_type'
                                      label={null}
                                      style={{ marginBottom: '0' }}
                                    >
                                      <Radio.Group
                                        onChange={(e) =>
                                          setOldUserHowOften(e.target.value)
                                        }
                                      >
                                        <Radio value={1}>
                                          {t(
                                            'cognito_form_admin_cognito_form_old_user_custom'
                                          )}
                                        </Radio>
                                        <Radio value={2}>
                                          {t(
                                            'cognito_form_admin_cognito_form_old_user_login'
                                          )}
                                        </Radio>
                                      </Radio.Group>
                                    </Form.Item>
                                  </div>

                                  {oldUserHowOften === 1 && (
                                    <div style={{ marginTop: '10px' }}>
                                      <div className='ant-form-item-label'>
                                        <label
                                          className='ant-form-item-required'
                                          style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                          }}
                                        >
                                          {t(
                                            'cognito_form_admin_cognito_form_label_repeat_every'
                                          )}
                                        </label>
                                      </div>

                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          marginTop: '4px',
                                        }}
                                      >
                                        <div className='form-group'>
                                          <Form.Item
                                            name='old_user_requirement_repeat_every'
                                            label={null}
                                            style={{ marginBottom: 0 }}
                                          >
                                            <InputNumber
                                              min={1}
                                              style={{ width: '60px' }}
                                            />
                                          </Form.Item>
                                        </div>

                                        <div className='form-group'>
                                          <Form.Item
                                            name='old_user_requirement_repeat_unit'
                                            label={null}
                                            style={{ marginBottom: 0 }}
                                          >
                                            <Select
                                              defaultValue={1}
                                              style={{ width: 100 }}
                                              options={[
                                                {
                                                  value: 1,
                                                  label: 'Day(s)',
                                                },
                                                {
                                                  value: 2,
                                                  label: 'Week(s)',
                                                },
                                              ]}
                                            />
                                          </Form.Item>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {type === 2 && (
                  <CareGuidePartnerTagInput
                    name='cognito_forms_care_guide_partner_tags'
                    rules={[
                      {
                        required: true,
                        message: t('feedback_validation_required'),
                      },
                    ]}
                    disabled={isLoading || isSubmitting}
                    label='cognito_form_admin_care_guide_partner_tags'
                  />
                )}
              </Form>
            </div>
          </Modal>
          <ConfirmationModal
            texts={confirmTexts}
            openConfirmationModal={confirmationModalOpen}
            onLeaveConfirmation={onLeaveConfirmModal}
            onSaveConfirmation={onSaveConfirmModal}
            onCancel={onCancelConfirmModal}
            setConfirmationModalOpen={setConfirmationModalOpen}
          />
        </>
      )}
    </Translation>
  );
};

export default CognitoFormModalFormAdmin;
